import { interestRateProblem } from "@financialharmony/tax-se";
import type { Customer, ReceivableDraft, Seller } from "./types";
import { RECEIVING_ACCOUNT_REQUIRED, RECEIVING_ACCOUNT_UNAVAILABLE, selectReceivingAccount, sellerPaidInto } from "./receivingAccount";

/**
 * Mirrors the backend's `validateReceivable` gate: address is always required,
 * `vat_number` only when the seller is actually VAT-registered.
 */
export function isSellerComplete(seller: Seller | null): boolean {
  return !!(seller && seller.addressStreet.trim() && (!seller.vatRegistered || seller.vatNumber.trim()));
}

/**
 * Mirrors the backend's `validateReceivable` gate: a payment reference (a real
 * connected account, a manual entry, or bankgiro/plusgiro fallback) is required.
 */
export function hasSellerPaymentReference(seller: Seller | null): boolean {
  return !!seller?.paymentReferenceLines?.length;
}

export interface DraftIssueValidationInput {
  seller: Seller | null;
  customer: Customer | null;
  draft: ReceivableDraft;
  hasBillableLine: boolean;
  /** Lines whose VAT classification is still in flight — issuing must wait for it. */
  classifyingLineCount: number;
  /** Lines whose VAT classification failed and needs a retry/edit before issuing. */
  classifyErrorLineCount: number;
}

/**
 * The full "what's blocking this invoice from being issued" gate (US1.EC3/EC7), shared so
 * both apps agree on exactly which messages block saving and in what order they're listed.
 */
/** The fixed reasons issuing is blocked, keyed so a consumer can word them in its own language. */
export const DRAFT_BLOCKERS = {
  sellerIncomplete: "Company info is missing (VAT number/address)",
  bankAccountMissing: "Bank account is missing — connect a bank account or add one manually",
  invoiceDateRequired: "Invoice date is required",
  customerRequired: "Choose a customer",
  billableLineRequired: "Add at least one invoice line with an amount",
  customerVatNumberRequired: "The customer's VAT number is required for reverse charge",
  vatClassifying: "VAT is still being determined for one or more lines",
  vatClassifyFailed: "VAT could not be determined for one or more lines — retry or edit the description",
  bankgiroForeignCurrency: "Bankgiro cannot be used for invoices in a currency other than SEK — change the payment method or currency",
} as const;

/** What precedes the interest rate's own reason in a blocker. */
export const LATE_INTEREST_BLOCKER_PREFIX = "Late payment interest: ";

export function validateDraftForIssuing(input: DraftIssueValidationInput): string[] {
  const { seller, customer, draft, hasBillableLine, classifyingLineCount, classifyErrorLineCount } = input;
  const missing: string[] = [];
  if (!isSellerComplete(seller)) missing.push(DRAFT_BLOCKERS.sellerIncomplete);
  // The receiving account decides what the invoice prints (FIN-283), so it is checked first.
  const receiving = selectReceivingAccount(seller, draft.receivingBankAccountId);
  if (receiving.kind === "choice_required") missing.push(RECEIVING_ACCOUNT_REQUIRED);
  else if (receiving.kind === "unavailable") missing.push(RECEIVING_ACCOUNT_UNAVAILABLE);
  else if (!hasSellerPaymentReference(sellerPaidInto(seller, draft.receivingBankAccountId)))
    missing.push(DRAFT_BLOCKERS.bankAccountMissing);
  if (!draft.invoiceDate) missing.push(DRAFT_BLOCKERS.invoiceDateRequired);
  if (!customer) missing.push(DRAFT_BLOCKERS.customerRequired);
  if (!hasBillableLine) missing.push(DRAFT_BLOCKERS.billableLineRequired);
  if (customer && customer.country !== "SE" && customer.type === "company" && !customer.vatNumber)
    missing.push(DRAFT_BLOCKERS.customerVatNumberRequired);
  if (classifyingLineCount > 0) missing.push(DRAFT_BLOCKERS.vatClassifying);
  if (classifyErrorLineCount > 0) missing.push(DRAFT_BLOCKERS.vatClassifyFailed);
  const interestError = interestRateProblem(draft.interestRatePercent, seller?.defaultInterestRatePercent ?? null);
  if (interestError) missing.push(`${LATE_INTEREST_BLOCKER_PREFIX}${interestError}`);
  // FIN-1580 (invoice-pdf-presentation.EC15): Bankgiro is a Sweden-domestic-only clearing
  // rail -- it cannot carry a foreign-currency payment at all. Mirrors the backend's
  // validateReceivable gate, same reasoning as hasSellerPaymentReference above.
  const resolvedMethod = draft.paymentMethod ?? seller?.resolvedPaymentMethod;
  if (resolvedMethod === "bankgiro" && draft.currency !== "SEK") {
    missing.push(DRAFT_BLOCKERS.bankgiroForeignCurrency);
  }
  return missing;
}

export interface DraftIssueReadiness {
  /** Nothing left in `missing` — the form's issue/save-draft action may proceed. */
  ready: boolean;
  /** Every reason issuing is currently blocked, in the fixed order both apps show. */
  missing: string[];
}

/**
 * `validateDraftForIssuing()` plus the ready/missing split both apps' UI branches on
 * (a disabled button vs. the listed reasons) — the single call each component's own
 * `useMemo` wraps, so the wrapper never repeats `DraftIssueValidationInput`'s six named
 * fields inline (that repetition, not the validation logic itself, was what FIN-800
 * found duplicated between `InvoiceGeneratorClient.tsx` and `CreateInvoice.tsx`).
 */
export function checkDraftIssueReadiness(input: DraftIssueValidationInput): DraftIssueReadiness {
  const missing = validateDraftForIssuing(input);
  return { ready: missing.length === 0, missing };
}
